import { Box, Typography, styled } from "@mui/material";
import btnContinue from "../../assets/img/1 APP INTRO & FIRST LAUNCH/btn-continue.svg";
import btnWide from "../../assets/img/1 APP INTRO & FIRST LAUNCH/btn-claim.svg";
import logoImg from "../../assets/img/header/logo.svg";
import { cutCorner, ga } from "../../components/ui/GaPrimitives";

export const Frame = styled(Box)({
  width: "100%",
  height: "100%",
  maxWidth: "100%",
  maxHeight: "100%",
  position: "relative",
  overflow: "hidden",
  flexShrink: 0,
});

export const NavText = styled("button")({
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  fontFamily: "'Rajdhani', sans-serif",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "2px",
  color: "#A8A8A8",
  lineHeight: "15px",
  outline: "none",
  boxShadow: "none",
  WebkitTapHighlightColor: "transparent",
  "&:hover, &:focus, &:focus-visible, &:active": {
    outline: "none",
    boxShadow: "none",
    background: "none",
  },
});

export const Eyebrow = styled(Typography)({
  fontFamily: "'Rajdhani', sans-serif",
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: "3px",
  color: "#B8EE3C",
  textAlign: "center",
  lineHeight: "14px",
});

export const TitleRow = styled(Box)({
  display: "flex",
  gap: 9,
  justifyContent: "center",
  alignItems: "flex-start",
});

export const TitleWord = styled("span")(({ accent }) => ({
  fontFamily: "'Rajdhani', sans-serif",
  fontWeight: 700,
  fontSize: 26,
  letterSpacing: "2px",
  lineHeight: "33px",
  color: accent ? "#B8EE3C" : "#FFFFFF",
  whiteSpace: "nowrap",
}));

export const BodyCopy = styled(Typography)({
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 400,
  fontSize: 12.5,
  lineHeight: "19px",
  color: "#A8A8A8",
  textAlign: "center",
  width: 310,
});

export const Dots = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 6,
});

export const Dot = styled(Box)(({ active }) => ({
  height: 3,
  width: active ? 18 : 12,
  background: active ? "#B8EE3C" : "#2A2E22",
}));

export const DialogCard = styled(Box)({
  position: "relative",
  width: 320,
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "26px 20px 20px",
  boxSizing: "border-box",
  "& > img.dialog-bg": {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "fill",
    pointerEvents: "none",
  },
});

export function CutButton({ wide = false, children, ...props }) {
  return (
    <Box
      component="button"
      type="button"
      {...props}
      onMouseDown={(event) => {
        event.preventDefault();
        props.onMouseDown?.(event);
      }}
      sx={{
        position: "relative",
        width: wide ? { xs: 280, md: 220 } : { xs: 215, md: 220 },
        maxWidth: "100%",
        height: wide ? { xs: 48, md: 48 } : 49,
        border: "none",
        padding: 0,
        cursor: "pointer",
        background: "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        outline: "none",
        boxShadow: "none",
        WebkitTapHighlightColor: "transparent",
        "&:hover, &:focus, &:focus-visible, &:active": {
          outline: "none",
          boxShadow: "none",
          background: "transparent",
        },
        ...props.sx,
      }}
    >
      <Box
        component="img"
        className="shape"
        src={wide ? btnWide : btnContinue}
        alt=""
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "fill",
          pointerEvents: "none",
        }}
      />
      {children}
    </Box>
  );
}

export function ButtonLabel({ children, wide = false }) {
  return (
    <Box
      component="span"
      sx={{
        position: "relative",
        zIndex: 1,
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: 700,
        fontSize: wide ? 14 : 15,
        letterSpacing: wide ? "2.5px" : "3px",
        color: "#0D0F0A",
        lineHeight: wide ? "18px" : "19px",
      }}
    >
      {children}
    </Box>
  );
}

export function IntroLogo({ onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        width: 105,
        height: 40,
        p: "8px 9px",
        boxSizing: "border-box",
        bgcolor: ga.lime,
        borderRadius: "2px",
        flexShrink: 0,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Box
        component="img"
        src={logoImg}
        alt="GamezAcademy"
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          filter: "brightness(0)",
        }}
      />
    </Box>
  );
}

export function IntroLimeCta({ children, onClick, width = 280, height = 50 }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      onMouseDown={(event) => event.preventDefault()}
      sx={{
        width,
        maxWidth: "100%",
        height,
        border: 0,
        bgcolor: ga.lime,
        clipPath: cutCorner(12),
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: 700,
        fontSize: 14,
        letterSpacing: "2px",
        color: ga.bg,
        flexShrink: 0,
        outline: "none",
        boxShadow: "none",
        WebkitTapHighlightColor: "transparent",
        "&:hover, &:focus, &:focus-visible, &:active": {
          outline: "none",
          boxShadow: "none",
          bgcolor: ga.lime,
        },
      }}
    >
      {children}
    </Box>
  );
}

export function IntroGhostBtn({ children, onClick, width = 140, height = 38, sx }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width,
        height,
        border: "1px solid #3D4433",
        bgcolor: "transparent",
        clipPath: cutCorner(8),
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: "2px",
        color: "#C5C5C5",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function IntroBackBtn({ onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      onMouseDown={(event) => event.preventDefault()}
      aria-label="Back"
      sx={{
        width: 44,
        height: 36,
        border: "1px solid #3D4433",
        bgcolor: "transparent",
        clipPath: cutCorner(8),
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: 700,
        fontSize: 22,
        lineHeight: 1,
        color: "#C5C5C5",
        outline: "none",
        boxShadow: "none",
        WebkitTapHighlightColor: "transparent",
        "&:hover, &:focus, &:focus-visible, &:active": {
          outline: "none",
          boxShadow: "none",
          bgcolor: "transparent",
        },
      }}
    >
      ‹
    </Box>
  );
}

export function IntroDesktopShell({ children, onSkip, onBack, showSkip, showBack }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#0D0F0A",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        alignSelf: "stretch",
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 16,
          left: "20px",
          right: "20px",
          zIndex: 2,
          display: "flex",
          alignItems: "center",
        }}
      >
        <IntroLogo />
        <Box sx={{ flex: 1 }} />
        {showSkip && (
          <IntroGhostBtn onClick={onSkip} width={88} height={36}>
            SKIP
          </IntroGhostBtn>
        )}
      </Box>
      {showBack && (
        <Box
          sx={{
            position: "absolute",
            top: 72,
            left: "20px",
            zIndex: 2,
          }}
        >
          <IntroBackBtn onClick={onBack} />
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          boxSizing: "border-box",
          pt: { md: "88px", lg: "96px" },
          pb: { md: "40px", lg: "48px" },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
